/**
 * useGamificationSocket — WebSocket subscription for real-time gamification updates
 *
 * Joins the gamification:{modelId} room and listens for:
 * - GAMIFICATION_PROGRESS — state updates
 * - GAMIFICATION_MILESTONE — milestone reached
 * - GAMIFICATION_COMPLETED — model completed
 *
 * Patches the React Query cache in-place for instant UI updates.
 * Same pattern as useHubPulse.ts.
 */
import {useEffect, useRef, useContext, useCallback} from "react";
import {useQueryClient} from "@tanstack/react-query";
import {WebsocketProviderContext} from "@/components/WebsocketProvider";
import {GAMIFICATION_MODEL_QUERY_KEY, GamificationModelResponse} from "./useGetGamificationModel";

const WS_EVENTS = {
    PROGRESS: "GAMIFICATION_PROGRESS",
    MILESTONE: "GAMIFICATION_MILESTONE",
    COMPLETED: "GAMIFICATION_COMPLETED",
} as const;

/*
 * The server withholds unrevealed content from these types, so a reveal needs a refetch.
 * Refetch only when the reveal marker moved, not on every progress event, so busy models don't fan out requests.
 */
function revealAdvanced(type: string | undefined, before: Record<string, unknown> | undefined, after: Record<string, unknown>): boolean {
    switch (type) {
        case "CIPHER": {
            const count = (state?: Record<string, unknown>) => Array.isArray(state?.revealedClueObjectiveIds) ? state.revealedClueObjectiveIds.length : 0;
            return count(after) > count(before);
        }
        case "MYSTERY_BOX":
            return Number(after.revealedSteps ?? 0) > Number(before?.revealedSteps ?? 0);
        case "COUNTDOWN":
        case "SABOTEUR":
            return after.revealed === true && before?.revealed !== true;
        default:
            return false;
    }
}

interface GamificationProgressPayload {
    modelId: string;
    type?: string;
    /** Matches GamificationModelResponse.state shape from @devour/client */
    state: { [key: string]: object };
    participantCount?: number;
}

interface GamificationMilestonePayload {
    modelId: string;
    milestone: {
        threshold: number;
        label: string;
        reached: boolean;
        reachedAt: number;
    };
}

interface GamificationCompletedPayload {
    modelId: string;
    status: string;
    completionCount?: number;
}

export interface UseGamificationSocketReturn {
    isConnected: boolean;
}

export function useGamificationSocket(modelId?: string): UseGamificationSocketReturn {
    const queryClient = useQueryClient();
    const {socket, socketConnection} = useContext(WebsocketProviderContext);
    const joinedRef = useRef<string | undefined>(undefined);
    /*
     * Distinct from `joinedRef`, which the cleanup clears so it can emit the
     * leave. This one is never cleared: it remembers that we have joined this
     * model before, which is what makes a second join a RECONNECT.
     */
    const everJoinedRef = useRef<string | undefined>(undefined);

    const patchCache = useCallback(
        (updater: (old: GamificationModelResponse) => GamificationModelResponse) => {
            queryClient.setQueriesData<GamificationModelResponse>(
                {queryKey: [GAMIFICATION_MODEL_QUERY_KEY, modelId]},
                (old) => old ? updater(old) : old,
            );
        },
        [queryClient, modelId],
    );

    useEffect(() => {
        if (!socket || !socketConnection || !modelId) return;

        // Join room
        socket.emit("gamification:join", modelId);
        joinedRef.current = modelId;

        /*
         * REPAIR ON REJOIN. Every handler below PATCHES the cache from the
         * payload, so a push missed while disconnected is lost outright — the
         * wheel kept its old participant count, and a milestone reached during
         * the blackout never lit. A phone drops its socket the moment the app
         * backgrounds, so this is the common case, not the rare one. Only on a
         * REJOIN: the first join coincides with the query's own first request.
         */
        const isRejoin = everJoinedRef.current === modelId;
        everJoinedRef.current = modelId;
        if (isRejoin) {
            queryClient.invalidateQueries({queryKey: [GAMIFICATION_MODEL_QUERY_KEY, modelId]});
        }

        const onProgress = (payload: GamificationProgressPayload) => {
            if (payload.modelId !== modelId) return;
            const before = queryClient.getQueriesData<GamificationModelResponse>({queryKey: [GAMIFICATION_MODEL_QUERY_KEY, modelId]})[0]?.[1]?.state;
            patchCache((old) => ({
                ...old,
                state: payload.state,
                ...payload.participantCount != null ? {participantCount: payload.participantCount} : {},
            }));
            if (revealAdvanced(payload.type, before, payload.state)) {
                queryClient.invalidateQueries({queryKey: [GAMIFICATION_MODEL_QUERY_KEY, modelId]});
            }
        };

        const onMilestone = (payload: GamificationMilestonePayload) => {
            if (payload.modelId !== modelId) return;
            patchCache((old) => ({
                ...old,
                milestones: old.milestones.map((m) =>
                    m.threshold === payload.milestone.threshold
                        ? {...m, reached: true, reachedAt: payload.milestone.reachedAt}
                        : m),
            }));
        };

        const onCompleted = (payload: GamificationCompletedPayload) => {
            if (payload.modelId !== modelId) return;
            patchCache((old) => ({
                ...old,
                status: payload.status as GamificationModelResponse["status"],
                ...payload.completionCount != null ? {completionCount: payload.completionCount} : {},
            }));
        };

        socket.on(WS_EVENTS.PROGRESS, onProgress);
        socket.on(WS_EVENTS.MILESTONE, onMilestone);
        socket.on(WS_EVENTS.COMPLETED, onCompleted);

        return () => {
            socket.off(WS_EVENTS.PROGRESS, onProgress);
            socket.off(WS_EVENTS.MILESTONE, onMilestone);
            socket.off(WS_EVENTS.COMPLETED, onCompleted);

            if (joinedRef.current) {
                socket.emit("gamification:leave", joinedRef.current);
                joinedRef.current = undefined;
            }
        };
    }, [socket, socketConnection, modelId, patchCache, queryClient]);

    return {isConnected: socketConnection && Boolean(modelId)};
}
